import React from 'react';
import '../styles/Services.css';

function Services({ language }) {
  // Plateformes technologiques (anciennement Products)
  const platforms = [
    {
      id: 1,
      name: 'MuleSoft Anypoint Platform',
      logo: 'https://wp.sfdcdigital.com/en-us/wp-content/uploads/sites/4/2024/06/icon-mulesoft.svg?w=1024',
      descEN: 'Enterprise integration platform for API-led connectivity. Connect applications, data, and devices across on-premises and cloud environments with industry-leading ESB and iPaaS capabilities.',
      descFR: 'Plateforme d\'intégration d\'entreprise pour la connectivité basée sur les API. Connectez applications, données et appareils sur site et dans le cloud avec des capacités ESB et iPaaS de pointe.',
      features: [
        { en: 'API Management', fr: 'Gestion API' },
        { en: 'Enterprise Service Bus', fr: 'Bus de services d\'entreprise' },
        { en: 'DataWeave Transformation', fr: 'Transformation DataWeave' },
        { en: 'CloudHub Deployment', fr: 'Déploiement CloudHub' }
      ]
    },
    {
      id: 2,
      name: 'Workato',
      logo: 'https://raw.githubusercontent.com/Helmifalfie7/nexofortis/main/logodynamics/workato.png',
      descEN: 'Leading low-code/no-code integration and automation platform. Build powerful integrations and automated workflows across 1000+ applications without coding, enabling business users and IT teams to collaborate seamlessly.',
      descFR: 'Plateforme leader d\'intégration et d\'automatisation low-code/no-code. Créez de puissantes intégrations et flux de travail automatisés sur plus de 1000 applications sans codage, permettant aux utilisateurs métiers et aux équipes IT de collaborer efficacement.',
      features: [
        { en: 'Automation Low-Code', fr: 'Automatisation Low-Code' },
        { en: '1000+ Connectors', fr: '1000+ Connecteurs' },
        { en: 'Workflow Orchestration', fr: 'Orchestration de Flux' },
        { en: 'Business Process Automation', fr: 'Automatisation Processus Métier' }
      ]
    }
  ];

  // Services spécialisés (3 services détaillés)
  const services = [
    {
      id: 1,
      icon: 'fa-plug',
      titleEN: 'MuleSoft Integration Services',
      titleFR: 'Services d\'Intégration MuleSoft',
      descEN: 'Connect your enterprise applications through API-led connectivity and unlock seamless data flow across your organization',
      descFR: 'Connectez vos applications d\'entreprise via une connectivité API et libérez un flux de données transparent dans votre organisation',
      forWhoEN: 'Companies with complex enterprise systems (ERP, CRM, legacy applications)',
      forWhoFR: 'Entreprises avec des systèmes d\'entreprise complexes (ERP, CRM, applications héritées)',
      outcomesEN: [
        'Reduce integration time by 50%',
        'Enable real-time data synchronization',
        'Build reusable API assets'
      ],
      outcomesFR: [
        'Réduire le temps d\'intégration de 50%',
        'Activer la synchronisation des données en temps réel',
        'Créer des actifs API réutilisables'
      ],
      includedEN: [
        'Integration strategy & architecture design',
        'API development & implementation',
        'Anypoint Platform configuration',
        'Testing & deployment'
      ],
      includedFR: [
        'Stratégie d\'intégration & conception d\'architecture',
        'Développement & implémentation d\'API',
        'Configuration de la plateforme Anypoint',
        'Tests & déploiement'
      ]
    },
    {
      id: 2,
      icon: 'fa-robot',
      titleEN: 'Workato Automation Solutions',
      titleFR: 'Solutions d\'Automatisation Workato',
      descEN: 'Automate business workflows across cloud and on-premise applications with low-code/no-code automation',
      descFR: 'Automatisez les flux de travail métier sur les applications cloud et sur site avec une automatisation low-code/no-code',
      forWhoEN: 'Operations teams needing to eliminate manual processes',
      forWhoFR: 'Équipes opérationnelles nécessitant d\'éliminer les processus manuels',
      outcomesEN: [
        'Reduce manual tasks by 70%',
        'Accelerate process completion',
        'Improve data accuracy'
      ],
      outcomesFR: [
        'Réduire les tâches manuelles de 70%',
        'Accélérer l\'achèvement des processus',
        'Améliorer la précision des données'
      ],
      includedEN: [
        'Process discovery & mapping',
        'Recipe development & deployment',
        'User training & enablement',
        'Ongoing optimization'
      ],
      includedFR: [
        'Découverte & cartographie des processus',
        'Développement & déploiement de recettes',
        'Formation & habilitation des utilisateurs',
        'Optimisation continue'
      ]
    },
    {
      id: 3,
      icon: 'fa-headset',
      titleEN: 'Managed Operations & Technical Support',
      titleFR: 'Opérations Gérées & Support Technique',
      descEN: 'Proactive monitoring, management, and optimization of your integration infrastructure',
      descFR: "Surveillance, gestion et optimisation proactives de votre infrastructure d'intégration",
      forWhoEN: 'Enterprises requiring continuous uptime and proactive support',
      forWhoFR: 'Entreprises nécessitant une disponibilité continue et un support proactif',
      outcomesEN: [
        '99.9% uptime guarantee',
        'Proactive issue resolution',
        'Performance optimization'
      ],
      outcomesFR: [
        'Garantie de disponibilité 99,9%',
        'Résolution proactive des problèmes',
        'Optimisation des performances'
      ],
      includedEN: [
        'Proactive monitoring & alerting',
        'Incident management & resolution',
        'Performance tuning',
        'Regular health checks'
      ],
      includedFR: [
        'Surveillance & alertes proactives',
        'Gestion & résolution d\'incidents',
        'Réglage des performances',
        'Contrôles de santé réguliers'
      ]
    }
  ];

  return (
    <section id="services" className="services-section">
      <div className="container">

        {/* SECTION 1: NOTRE EXPERTISE TECHNOLOGIQUE (Plateformes) */}
        <div className="platforms-section">
          <h2 className="section-title">
            <span lang="en">Our Technical Expertise</span>
            <span lang="fr">Notre Expertise technique</span>
          </h2>

          <p className="section-subtitle">
            <span lang="en">
              We master the leading integration and automation platforms on the market to transform your digital ecosystem
            </span>
            <span lang="fr">
              Nous maîtrisons les plateformes d'intégration et d'automatisation leaders du marché pour transformer votre écosystème digital
            </span>
          </p>

          <div className="platforms-grid">
            {platforms.map((platform) => (
              <div key={platform.id} className="platform-card">
                <div className="platform-logo">
                  <img src={platform.logo} alt={platform.name} />
                </div>
                <h3>{platform.name}</h3>
                <p className="platform-description">
                  <span lang="en">{platform.descEN}</span>
                  <span lang="fr">{platform.descFR}</span>
                </p>
                <div className="platform-features">
                  {platform.features.map((feature, idx) => (
                    <div key={idx} className="feature-badge">
                      <span lang="en">{feature.en}</span>
                      <span lang="fr">{feature.fr}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 2: NOS SERVICES SPÉCIALISÉS (3 services détaillés) */}
        <div className="detailed-services-section">
          <h2 className="section-title">
            <span lang="en">Integration, Automation, Support</span>
            <span lang="fr">Intégration, Automatisation, Support﻿</span>
          </h2>

          <p className="section-subtitle">
            <span lang="en">
              From API integration to intelligent automation, we deploy MuleSoft and Workato to create measurable impact on your business
            </span>
            <span lang="fr">
              De l'intégration API à l'automatisation intelligente, nous déployons MuleSoft et Workato pour créer un impact mesurable sur votre business
            </span>
          </p>

          <div className="services-grid-detailed">
            {services.map((service) => (
              <div key={service.id} className="service-card-detailed">
                <div className="service-header">
                  <div className="service-icon-large">
                    <i className={`fas ${service.icon}`}></i>
                  </div>
                  <h3>
                    <span lang="en">{service.titleEN}</span>
                    <span lang="fr">{service.titleFR}</span>
                  </h3>
                </div>

                <p className="service-description">
                  <span lang="en">{service.descEN}</span>
                  <span lang="fr">{service.descFR}</span>
                </p>

                <div className="service-section">
                  <h4 className="section-label">
                    <i className="fas fa-users"></i>
                    <span lang="en">Who It's For</span>
                    <span lang="fr">Pour Qui</span>
                  </h4>
                  <p className="service-for-who">
                    <span lang="en">{service.forWhoEN}</span>
                    <span lang="fr">{service.forWhoFR}</span>
                  </p>
                </div>

                <div className="service-section">
                  <h4 className="section-label outcomes-label">
                    <i className="fas fa-chart-line"></i>
                    <span lang="en">Business Outcomes</span>
                    <span lang="fr">Résultats Métier</span>
                  </h4>
                  <ul className="outcomes-list">
                    {service.outcomesEN.map((outcome, idx) => (
                      <li key={idx}>
                        <i className="fas fa-check-circle"></i>
                        <span lang="en">{outcome}</span>
                        <span lang="fr">{service.outcomesFR[idx]}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="service-section">
                  <h4 className="section-label included-label">
                    <i className="fas fa-box-open"></i>
                    <span lang="en">What's Included</span>
                    <span lang="fr">Ce qui est inclus</span>
                  </h4>
                  <ul className="included-list">
                    {service.includedEN.map((item, idx) => (
                      <li key={idx}>
                        <i className="fas fa-arrow-right"></i>
                        <span lang="en">{item}</span>
                        <span lang="fr">{service.includedFR[idx]}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}

export default Services;
